import { useMemo } from "react";

import { Row, ExpandedTooltip, TooltipSection, Text } from "@bridgesplit/ui";
import {
    ActionType,
    FarmingInfoExpanded,
    getLoopAtCapacity,
    LoopExpanded,
    LoopscalePointsAction,
    OracleQuote,
    useFarmingInfoByActionTypeAndAddress,
    useLoopOraclePriceQuotes,
    useLoopscaleWalletContext,
    usePrincipalBorrowedWithLeverage,
    useUnderlyingTokenInfo
} from "@bridgesplit/abf-react";

import { BaseApy } from "./BaseApy";
import { LoopsAdditionalRewardsDisplay, RateDisplay } from "./common";
import { RewardsDailyPoints, RewardsDailyPointsBreakdown } from "./daily-rewards";
import { POINT_BASIS } from "./types";
import { RewardsMaxLeverage } from "./loops/RewardsMaxLeverage";
import { RewardsProtocolPoints } from "./RewardsProtocolPoints";

export function RewardsLoopsApy({
    loopExpanded,
    useAverage
}: {
    loopExpanded: LoopExpanded | undefined;
    useAverage: boolean;
}) {
    const { principalOracle } = useLoopOraclePriceQuotes(loopExpanded);

    const loopApy = getLoopApy(loopExpanded, useAverage, principalOracle);

    const { farmingInfo, hasFarmingInfo } = useFarmingInfoByActionTypeAndAddress(
        loopExpanded?.vaultIdentifier,
        ActionType.Loop
    );
    const isAtCapacity = getLoopAtCapacity(loopExpanded, principalOracle);

    const sections = useMemo(() => {
        return getLoopRewardSections(loopExpanded, farmingInfo, hasFarmingInfo, isAtCapacity);
    }, [loopExpanded, farmingInfo, hasFarmingInfo, isAtCapacity]);

    return (
        <ExpandedTooltip sections={sections}>
            <Row spacing={1}>
                <LoopsAdditionalRewardsDisplay loopExpanded={loopExpanded} farmingInfo={farmingInfo} />
                <RateDisplay apy={loopApy} variant="body1" color="body" />
            </Row>
        </ExpandedTooltip>
    );
}

function getLoopRewardSections(
    loopExpanded: LoopExpanded | undefined,
    farmingInfo: FarmingInfoExpanded | undefined,
    hasFarmingInfo: boolean,
    isAtCapacity: boolean
): TooltipSection[] {
    const sections: TooltipSection[] = [];

    sections.push({
        type: "render",
        render: () => <BaseApy apy={loopExpanded?.loopVault.wAvgApy} variant="body2" />,
        px: 1.5,
        spacing: 0.5
    });

    sections.push({
        type: "render",
        render: () => <RewardsMaxLeverage maxLeverage={loopExpanded?.loopVault.maxLeverage} />,
        px: 1.5,
        spacing: 0.5
    });

    sections.push({ type: "divider" });

    sections.push({
        type: "render",
        render: () => <LoopsDailyPoints loopExpanded={loopExpanded} />,
        px: 1.5,
        spacing: 0.5
    });

    if (hasFarmingInfo) {
        sections.push({ type: "divider" });
        sections.push({
            type: "render",
            render: () => <RewardsProtocolPoints farmingInfo={farmingInfo} />,
            px: 1.5,
            spacing: 0.5
        });
    }

    if (isAtCapacity) {
        sections.push({ type: "divider" });
        sections.push({
            type: "render",
            render: () => <RewardsNoApy />,
            px: 1.5,
            spacing: 0.5
        });
    }

    return sections;
}

function getLoopApy(
    loopExpanded: LoopExpanded | undefined,
    useAverage: boolean,
    principalOracle: OracleQuote | undefined
) {
    const isLoopAtCapacity = getLoopAtCapacity(loopExpanded, principalOracle);
    if (isLoopAtCapacity) {
        return undefined;
    }

    if (useAverage) {
        return loopExpanded?.loopVault.wAvgApy;
    }

    return loopExpanded?.loopVault.maxLeveragedApyPct;
}

function LoopsDailyPoints({ loopExpanded }: { loopExpanded: LoopExpanded | undefined }) {
    const {
        data: { userWalletAddress: activeWallet }
    } = useLoopscaleWalletContext();
    const { oraclePrices } = useUnderlyingTokenInfo(
        loopExpanded ? [loopExpanded.collateralToken.mint, loopExpanded.principalToken.mint] : []
    );
    const { principalBorrowedWithLeverageUsd } = usePrincipalBorrowedWithLeverage({
        loopExpanded: loopExpanded,
        loopLeverage: loopExpanded?.loopVault.maxLeverage,
        totalAmount: POINT_BASIS / (oraclePrices?.get(loopExpanded?.collateralToken.mint ?? "")?.usdPrice ?? 1),
        skip: !activeWallet,
        oracleQuotes: oraclePrices
    });

    if (principalBorrowedWithLeverageUsd) {
        return (
            <RewardsDailyPoints
                loopscalePointsAction={LoopscalePointsAction.Borrow}
                identifier={loopExpanded?.vaultIdentifier}
                principalBorrowedWithLeverageUsd={principalBorrowedWithLeverageUsd}
                collateralMetadata={loopExpanded?.collateralToken}
            />
        );
    }

    // No route found for loop
    return (
        <RewardsDailyPointsBreakdown
            loopscalePointsAction={LoopscalePointsAction.Borrow}
            identifier={loopExpanded?.vaultIdentifier}
            collateralMetadata={loopExpanded?.collateralToken}
        />
    );
}

function RewardsNoApy() {
    return (
        <Text variant="body2" color="caption">
            Loop is currently at deposit capacity, more lenders are required to increase available liquidity.
        </Text>
    );
}
